import { useCallback, useEffect, useRef, useState } from 'react'
import { isRetryable } from '../api/client'
import { retryDelayMs, shouldRetry } from './retrySchedule'
import type { SurfaceState } from '../components'

/**
 * A request → the four states every data surface ships (design-system.md §8).
 *
 * 🔴 THIS HOOK IS WHY D1–D7 ARE NOT "EIGHT HARDCODED CHILDREN".
 *
 * The TODO's warning about the predecessor is the sharpest sentence in §8.4:
 * *"wire every screen to real endpoints the week they land — the predecessor
 * rendered 8 hardcoded children while working endpoints sat uncalled."* The
 * near-miss is a screen that takes `rows` as a prop with a fixture file beside
 * it, which is the same failure one level up.
 *
 * So every teacher screen holds one of these, issues its real request to its
 * real path, and renders whatever comes back — **including the 401 it gets
 * today**, because staff login is Unit 9. The wiring is finished; the
 * credential is not.
 *
 * ⚠️ Deliberately not a cache. `caching.md` §3 keeps scoping decisions
 * uncached, and a teacher refreshing a dashboard expects today's number. A
 * query library would arrive with a default staleTime and a story about why the
 * class overview is four minutes old.
 */

export interface Resource<T> {
  state: SurfaceState
  data: T | null
  error: Error | null
  /** When the last successful load happened — `DataSurface` requires it. */
  lastLoadedAt: Date | null
  retry: () => void
}

export interface UseResourceOptions<T> {
  /** Skip the request entirely — e.g. no class selected yet. */
  enabled?: boolean
  /** Decides whether a successful response is the `empty` state. */
  isEmpty?: (data: T) => boolean
}

export function useResource<T>(
  load: (signal: AbortSignal) => Promise<T>,
  deps: readonly unknown[],
  options: UseResourceOptions<T> = {},
): Resource<T> {
  const { enabled = true, isEmpty } = options

  const [data, setData] = useState<T | null>(null)
  const [error, setError] = useState<Error | null>(null)
  const [loading, setLoading] = useState(enabled)
  const [lastLoadedAt, setLastLoadedAt] = useState<Date | null>(null)
  const [attempt, setAttempt] = useState(0)

  /*
   * The loader is captured in a ref rather than listed as a dependency. Callers
   * write `useResource(() => getRoster(classId), [classId])` and that arrow is
   * a new function every render — as a dependency it would refetch forever, and
   * on a screen showing a class of children that is a request per frame.
   */
  const loadRef = useRef(load)
  loadRef.current = load

  const emptyRef = useRef(isEmpty)
  emptyRef.current = isEmpty

  /*
   * 🔴 THE DEPENDENCY IS A KEY, NOT THE ARRAY.
   *
   * Callers write `useResource(() => getRoster(classId), [classId])`, and that
   * array literal is a NEW OBJECT every render. Depending on it directly
   * refetches on every render — a request per frame on a screen showing a class
   * of children, which is worse than the N+1 the backend half of §8.4 exists to
   * avoid. Serialising is safe because the values callers pass are ids.
   */
  const depsKey = JSON.stringify(deps)

  useEffect(() => {
    if (!enabled) {
      setLoading(false)
      return
    }

    const controller = new AbortController()
    let live = true
    let timer: ReturnType<typeof setTimeout> | undefined

    setLoading(true)
    setError(null)

    /*
     * 🔴 CHANGE 19 §13 — A DROPPED PACKET IS NO LONGER AN ERROR SCREEN.
     *
     * `client.ts` has always classified failures into "the moment was wrong" and
     * "the request was wrong", in a function its own header calls *"the most
     * consequential function in the offline stack"* — and `isRetryable` was
     * called by nothing but its own test. Every panel in the product gave up on
     * the first failure, so one blip on a school's access point put an error and
     * a Retry button in front of a teacher when the second attempt would have
     * worked.
     *
     * ⚠️ IT RETRIES SILENTLY AND STAYS IN `loading` WHILE IT DOES. A panel that
     * flashed an error and then recovered would be worse than one that waited:
     * `DataSurface` exists so a screen has one honest state at a time, and
     * "trying again" is loading, not failure.
     *
     * ⚠️ TRANSIENT ONLY, AND CAPPED AT TWO. A permanent failure — a rejected
     * body, a forbidden scope — surfaces immediately, exactly as before.
     * `retrySchedule.ts` holds both numbers and the argument for them.
     */
    const attemptOnce = (soFar: number): void => {
      loadRef
        .current(controller.signal)
        .then((result) => {
          if (!live) return
          setData(result)
          setLastLoadedAt(new Date())
          setLoading(false)
        })
        .catch((err: unknown) => {
          // An abort is this component moving on, not a failure the teacher needs
          // to see — surfacing it would flash an error banner on every navigation.
          if (!live || controller.signal.aborted) return

          if (shouldRetry(err, soFar, isRetryable)) {
            timer = setTimeout(() => {
              if (live && !controller.signal.aborted) attemptOnce(soFar + 1)
            }, retryDelayMs(soFar + 1))
            return
          }

          setError(err instanceof Error ? err : new Error(String(err)))
          setLoading(false)
        })
    }

    attemptOnce(0)

    return () => {
      live = false
      /* 🔴 THE TIMER IS CLEARED AS WELL AS THE REQUEST ABORTED. A pending retry
         that fired after unmount would call `setState` on a dead component and,
         worse, issue a request for a screen nobody is looking at. */
      if (timer !== undefined) clearTimeout(timer)
      controller.abort()
    }
  }, [enabled, attempt, depsKey])

  const retry = useCallback(() => setAttempt((n) => n + 1), [])

  /*
   * 🔴 PRECEDENCE MATTERS, AND ERROR BEATS STALE DATA.
   *
   * `DataSurface`: *"a zero never contradicts the panel beside it"*. If a
   * refetch fails while old data is still in state, showing the old data with
   * no error is how a teacher reads Tuesday's numbers on Thursday and plans
   * around them.
   */
  const state: SurfaceState = loading
    ? 'loading'
    : error
      ? 'error'
      : data === null
        ? 'empty'
        : emptyRef.current?.(data)
          ? 'empty'
          : 'ready'

  return { state, data, error, lastLoadedAt, retry }
}
